import { useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import AsyncButton from '@wojtekmaj/react-async-button';
import T, { useTranslation } from '@wojtekmaj/react-t';

import { toast } from '@rewardopl/react-toast';

import Button from '@rewardopl/react-ui/button';
import InputField from '@rewardopl/react-ui/input_field';

import { post } from '@rewardopl/utils/network';

import { ButtonWrapper, Form } from '../../login/email/form.styles';

import BirthdayField from './birthday_field';

import { cardSubscriptionsState, maybeCurrentUserState } from '../../../../store';

import { APP_ID, VENDOR_CARD_ID } from '../../../../constants';

import type { CardSubscription, User } from '@rewardopl/types';

const pendingConfig = {
  disabled: true,
};

export default function RegisterWithDetailsStepDetailsForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const setCurrentUser = useSetAtom(maybeCurrentUserState);
  const setCardSubscriptions = useSetAtom(cardSubscriptionsState);
  const registerErrorString = useTranslation('Failed to register');
  const userAlreadyExistsString = useTranslation('User already exists');
  const passwordInvalidString = useTranslation(
    'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character.',
  );

  const {
    email,
    mobile,
    password,
    termsAccepted,
    marketingAccepted,
    marketingEmailAccepted,
    marketingSmsAccepted,
    referralId,
    returnPath = '/',
  } = (location.state || {}) as {
    email: string;
    mobile: string;
    password: string;
    termsAccepted: boolean;
    marketingAccepted: boolean;
    marketingEmailAccepted: boolean;
    marketingSmsAccepted: boolean;
    referralId?: string;
    returnPath?: string;
  };

  const action = '/api/users';

  async function onClick(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    event.preventDefault();

    const submitButton = event.currentTarget;
    const form = submitButton.form;

    if (!form || !form.reportValidity()) {
      return;
    }

    const formData = new FormData(form);
    const filteredFormData = new FormData();

    for (const [key, value] of formData.entries()) {
      if (value !== '') {
        filteredFormData.append(key, value);
      }
    }

    filteredFormData.append('email', email);
    filteredFormData.append('mobile', mobile);
    filteredFormData.append('password', password);
    filteredFormData.append('terms_accepted', termsAccepted.toString());
    filteredFormData.append('marketing_accepted', marketingAccepted.toString());
    filteredFormData.append('marketing_email_accepted', marketingEmailAccepted.toString());
    filteredFormData.append('marketing_sms_accepted', marketingSmsAccepted.toString());
    if (referralId) {
      filteredFormData.append('referral_id', referralId);
    }

    try {
      const user = (await post(action, {
        headers: {
          'app-id': APP_ID,
        },
        body: filteredFormData,
      })) as User;

      const response = (await post('/api/users/current/card_subscriptions', {
        body: { card_id: VENDOR_CARD_ID },
      })) as CardSubscription;

      setCurrentUser(user);

      setCardSubscriptions([response]);
    } catch (err) {
      const error = err;

      const errorString = (() => {
        if (!(error instanceof Error)) {
          return registerErrorString;
        }

        switch (error.message) {
          case 'Password does not meet complexity requirements':
            return passwordInvalidString;
          case 'User already exists':
            return userAlreadyExistsString;
          default:
            return registerErrorString;
        }
      })();

      toast.error(errorString);

      if (error instanceof Error && error.message === 'User already exists') {
        navigate('/login/email', { state: { email: formData.get('email'), returnPath } });
      }
    }
  }

  return (
    <Form action={action} method="POST">
      <InputField autoComplete="given-name" label="First name" name="first_name" type="text" />
      <BirthdayField
        hint="Get a gift for your birthday"
        label="Birthday"
        name="birthday"
        required={false}
      />
      <ButtonWrapper>
        <AsyncButton
          as={Button}
          className="primary"
          onClick={onClick}
          pendingConfig={pendingConfig}
          type="submit"
        >
          <T>Register</T>
        </AsyncButton>
      </ButtonWrapper>
    </Form>
  );
}
